import type { ReactNode } from "react";
import { useCallback, useMemo, useState, useSyncExternalStore } from "react";

import {
  CloseIcon,
  MaximizeIcon,
  MinimizeIcon,
  SpinnerIcon
} from "@/assets/icons";
import { Button } from "@/components/ui/button";

import { ReportSkeleton } from "./ReportSkeleton";
import { useFollowWhileWriting } from "./useFollowWhileWriting";

import type { ChangeMarks } from "./a2ui/ReportSurface";
import { ReportSurface } from "./a2ui/ReportSurface";
import type { ReportSurfaceStore } from "./a2ui/surfaceStore";
import { surfaceHasContent } from "./a2ui/surfaceStore";

interface AnalysisReportPanelProps {
  store: ReportSurfaceStore;
  onClose: () => void;
  isExpanded?: boolean;
  onToggleMaximize?: () => void;
}

const NOTHING_SEEN: readonly string[] = [];

function HeaderChip({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-teal-50 px-2.5 py-1 text-xs font-semibold text-teal-700">
      <span
        aria-hidden="true"
        className="size-1.5 animate-pulse rounded-full bg-teal-500 motion-reduce:animate-none"
      />
      {children}
    </span>
  );
}

/**
 * The report surface, rendered beside the map and owned by the host.
 *
 * It reads the store rather than the transcript, so the report stays on screen
 * while the conversation moves on to the next turn.
 */
export function AnalysisReportPanel({
  store,
  onClose,
  isExpanded: isExpandedProp,
  onToggleMaximize
}: AnalysisReportPanelProps) {
  const [internalExpanded, setInternalExpanded] = useState(false);

  const isExpanded = isExpandedProp ?? internalExpanded;

  const handleToggleMaximize =
    onToggleMaximize ?? (() => setInternalExpanded((prev) => !prev));

  const state = useSyncExternalStore(store.subscribe, store.getSnapshot);
  const { activeSurfaceId, previewVersion, changedBlockIds } = state;
  const scrollRef = useFollowWhileWriting(state.status === "writing");

  // What the reader has looked at belongs to one version; the next starts over.
  const versionKey = `${activeSurfaceId}:${state.version}`;
  const [seen, setSeen] = useState({ key: versionKey, ids: NOTHING_SEEN });
  const seenIds = seen.key === versionKey ? seen.ids : NOTHING_SEEN;
  const handleSeen = useCallback(
    (id: string) =>
      setSeen((current) => {
        const ids = current.key === versionKey ? current.ids : NOTHING_SEEN;
        return ids.includes(id)
          ? current
          : { key: versionKey, ids: [...ids, id] };
      }),
    [versionKey]
  );
  const changeMarks = useMemo<ChangeMarks>(
    () => ({ changedIds: changedBlockIds, seenIds, onSeen: handleSeen }),
    [changedBlockIds, seenIds, handleSeen]
  );
  const unseenCount = changedBlockIds.filter(
    (id) => !seenIds.includes(id)
  ).length;
  const surface = activeSurfaceId
    ? store.getSurface(activeSurfaceId)
    : undefined;

  // A surface is created by the first frame; it has nothing to draw until a
  // block arrives, and a `root` alone is not one. A run that ended without any
  // is not coming back.
  const hasContent = surfaceHasContent(surface);
  const isPreparing =
    surface !== undefined && !hasContent && !state.provisional;
  const isNextReportComing = state.incomingSurfaceId !== null;
  // Either way the report on screen is the old one, so it is dimmed.
  const isBeingReplaced = isNextReportComing || state.isRewriting;

  let body: ReactNode;
  if (!surface) {
    body = state.status !== "writing" && (
      <p className="text-sm text-txtBody">
        No report in this conversation yet.
      </p>
    );
  } else if (isPreparing) {
    body = <ReportSkeleton />;
  } else {
    // The wrapper stays put while the report is replaced, so dimming the shown
    // one never remounts it.
    body = (
      <div
        className={
          isBeingReplaced
            ? "pointer-events-none animate-pulse motion-reduce:animate-none"
            : undefined
        }
      >
        {/* Keyed so switching version or report mounts a fresh tree instead of
            rebinding the old one to a different surface model. */}
        <ReportSurface
          key={`${activeSurfaceId}:${previewVersion ?? "live"}`}
          surface={surface}
          animateArrivals={state.status === "writing"}
          changeMarks={changeMarks}
        />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-white rounded-xl">
      <header className="flex shrink-0 items-center justify-between gap-2 px-4 pt-4 pb-2">
        <div className="flex min-w-0 items-center gap-3">
          <h1 className="min-w-0 truncate text-xl font-semibold text-txtHeading">
            Analysis Summary
          </h1>
          {isPreparing && <HeaderChip>Preparing report</HeaderChip>}
          {unseenCount > 0 && !isBeingReplaced && (
            <HeaderChip>
              {unseenCount} unseen {unseenCount === 1 ? "change" : "changes"}
            </HeaderChip>
          )}
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            className="size-auto [&_svg]:size-6"
            size="icon"
            aria-label={
              isExpanded
                ? "Minimize Analysis Report Panel"
                : "Maximize Analysis Report Panel"
            }
            data-testid="report-maximize-button"
            onClick={handleToggleMaximize}
          >
            {isExpanded ? (
              <MinimizeIcon className="fill-dim-gray" />
            ) : (
              <MaximizeIcon className="fill-dim-gray" />
            )}
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className="size-auto [&_svg]:size-6"
            aria-label="Tutup report"
            onClick={onClose}
          >
            <CloseIcon className="fill-dim-gray" />
          </Button>
        </div>
      </header>

      {state.provisional && (
        <p className="shrink-0 border-b border-gainsboro bg-amber-50 px-4 py-2 text-xs text-amber-800">
          Belum tersimpan — report ini hilang saat halaman dimuat ulang.
        </p>
      )}

      <div
        ref={scrollRef}
        data-testid="report-scroll"
        className="min-h-0 flex-1 overflow-y-auto px-6 py-4"
      >
        {isBeingReplaced && (
          <div className="sticky top-0 z-10 mb-3 flex justify-center">
            <output className="inline-flex items-center gap-2 rounded-full border border-gainsboro bg-white px-3 py-1 text-xs font-medium text-txtBody shadow-sm">
              <SpinnerIcon className="spin-animation size-4" />
              {isNextReportComing
                ? "Creating new report…"
                : "Rewriting report…"}
            </output>
          </div>
        )}
        {body}
      </div>
    </div>
  );
}
